<template>
  <div
    v-click-outside="() => (active = false)"
    class="course-announcements announcements-dropdown announcement-btn"
  >
    <button
      @click="active = !active"
      :aria-label="ariaCountMessage"
      :aria-expanded="active.toString()"
      aria-controls="announcements-panel"
      class="gc-header__button gc-header__header-button--announcement"
      type="button"
    >
      <i aria-hidden="true" class="fa fa-fw fa-bell"></i>
      <div
        aria-hidden="true"
        class="notification-badge"
        v-if="unreadAnnouncementsCount"
      ></div>
    </button>
    <section
      id="announcements-panel"
      aria-label="Announcements"
      v-show="active"
      class="dropdown-card"
    >
      <LoadingDots v-if="fetchingAnnouncements" />
      <div
        v-else
        class="gc-header__announcement-list-wrapper dropdown-card__content"
        :class="{ no_announcements: !hasAnnouncements }"
      >
        <ul class="gc-header__announcement-list" v-if="hasAnnouncements">
          <li
            class="dropdown-card__list-item gc-header__announcement-list-item announcement-unread"
            v-for="announcement in announcementsList"
            :key="announcement.id"
          >
            <a
              class="announcements-dropdown__announcement-title"
              :href="getAnnouncementURL(announcement.id)"
              >{{ announcement.title }}</a
            >
            <p class="announcements-dropdown__announcement--date">
              {{ dateAtTime(announcement.createdAt) }}
            </p>
            <div
              class="announcements-dropdown__announcement-content"
              v-html="truncatedAnnouncement(announcement.bodyPlainText)"
            ></div>
          </li>
        </ul>
        <div class="gc-header__announcement-list" v-else>
          No unread announcements.
        </div>
        <div class="announcement-list__actions">
          <a class="button" :href="announcementsUrl">View all announcements</a>
        </div>
      </div>
    </section>
  </div>
</template>
<script setup>
import { computed, ref, onBeforeMount } from "vue";
import { dateAtTime } from "@/courses/utilities/dates.js";
import { pluralize } from "@/courses/utilities/base.js";
import {
  courseAnnouncementListUrl,
  courseAnnouncementDetailUrl,
  getAnnouncementsCurrentUserUrl,
} from "@/courses/urls.js";
import axios from "axios";
import { apiClient } from "@/courses/services/ApiClientV2.js";
import LoadingDots from "@/courses/components/structure/LoadingDots.vue";

const props = defineProps({
  courseId: {
    type: [Number, String],
    required: true,
  },
});
const active = ref(false);
const announcementsList = ref([]);
const unreadAnnouncementsCount = ref(0);
const announcementsUrl = courseAnnouncementListUrl.stringify({
  courseId: props.courseId,
});
const ariaCountMessage = computed(() => {
  if (unreadAnnouncementsCount.value) {
    return `View ${unreadAnnouncementsCount.value} unread ${pluralize(
      "announcement",
      unreadAnnouncementsCount.value,
    )}`;
  } else {
    return "0 unread announcements";
  }
});
const hasAnnouncements = computed(() => {
  return announcementsList.value.length > 0;
});

let abortController;
const fetchingAnnouncements = ref(true);
const getAnnouncements = async () => {
  if (abortController) {
    abortController.abort();
  }
  abortController = new AbortController();
  try {
    const { data } = await apiClient.get(
      getAnnouncementsCurrentUserUrl.stringify({ courseId: props.courseId }),
      { signal: abortController.signal },
    );
    announcementsList.value = data;
    unreadAnnouncementsCount.value = data.length;
  } catch (e) {
    if (axios.isCancel(e)) {
      // Request was cancelled, do nothing
    } else {
      // Handle error (e.g., log it)
      console.error("Error fetching announcements:", e);
    }
  } finally {
    fetchingAnnouncements.value = false;
  }
};
onBeforeMount(() => {
  getAnnouncements();
});
const getAnnouncementURL = (announcementId) => {
  return courseAnnouncementDetailUrl.stringify({
    courseId: props.courseId,
    announcementId: announcementId,
  });
};
const truncatedAnnouncement = (announcement) => {
  let length = 100;
  if (announcement.length > length) {
    return announcement.substr(0, length) + "…";
  } else {
    return announcement;
  }
};
</script>
